import * as React from 'react';
import { t } from '@deltasierra/shared';
import { isNullOrUndefined } from '@deltasierra/type-utilities';
import { isString } from 'formik';
import { BootstrapColor } from '../theme';
import { Button } from '../Button';
import { TextInput } from '../Input';
import { ModalWithHeader, ModalBody, ModalFooter } from './Modal';

export type ConfirmModalProps = Omit<ModalWithHeader, 'children'> & {
    /**
     * The label for the cancel button
     *
     * @default 'Cancel'
     */
    cancelText?: string;
    /**
     * The label for the confirm button
     *
     * @default 'Ok'
     */
    confirmText?: string;
    /**
     *
     * @default 'default'
     */
    confirmTheme?: BootstrapColor;
    /**
     * If set, will require user to enter text before continuing
     */
    confirmationText?: string;
    /**
     * The message displayed in the modal
     */
    message: JSX.Element | string;
    onCancel?: () => void;
    onConfirm?: () => void;
};

/**
 * Confirmation modal component.
 *
 * @param props - Props
 * @returns React.ReactElement
 */
export const ConfirmModal: React.FunctionComponent<ConfirmModalProps> = props => {
    const {
        cancelText = t('COMMON.CANCEL'),
        closeOnBackdropClick = true,
        confirmationText,
        confirmText = t('COMMON.OK'),
        confirmTheme = 'primary',
        message,
        onCancel,
        onClose,
        onConfirm,
    } = props;

    const isConfirmationRequired = !isNullOrUndefined(confirmationText);

    const [confirmationInputText, updateConfirmationInputText] = React.useState('');

    const onShow = () => {
        if (isConfirmationRequired) {
            updateConfirmationInputText('');
        }
    };

    const handleOnCancel = () => {
        onCancel?.();
        onClose?.();
    };

    const handleOnConfirm = () => {
        onConfirm?.();
        onClose?.();
    };

    return (
        <ModalWithHeader {...props} closeOnBackdropClick={closeOnBackdropClick} onShow={onShow}>
            <ModalBody>
                {isString(message) ? <p>{message}</p> : message}
                {isConfirmationRequired && (
                    <>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: t('COMMON.CONFIRM_MODAL_INPUT', { input: confirmationText }),
                            }}
                        />
                        <TextInput initialValue={confirmationInputText} onChange={updateConfirmationInputText} />
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button label={cancelText} onClick={handleOnCancel} />
                <Button
                    disabled={isConfirmationRequired && confirmationInputText !== confirmationText}
                    label={confirmText}
                    theme={confirmTheme}
                    onClick={handleOnConfirm}
                />
            </ModalFooter>
        </ModalWithHeader>
    );
};

ConfirmModal.displayName = 'ConfirmModal';
